import { useState } from 'react';
import { MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useReduxStore } from '../hooks/useReduxStore';

export default function BulletinTable({ bulletinsData = [], onAddBulletin, onEditBulletin, onDeleteBulletin }) {
  const { getState } = useReduxStore();
  const { loading } = getState('AUTH');

  const [searchTerm, setSearchTerm] = useState('');

  const filtered = bulletinsData.filter(
    (bulletin) =>
      bulletin.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bulletin.description || bulletin.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      bulletin.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen p-4 py-8">
      {/* Search Bar and Add Button */}
      <div className="mb-8">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-6 h-6 text-white absolute left-4 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search Bulletins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-14 pl-12 pr-8 rounded-lg border-2 border-borderGray bg-[#384355] text-white placeholder-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={onAddBulletin}
            className="bg-primaryBlue text-white px-6 py-3 rounded-lg hover:bg-primaryBlue/90 transition-colors flex items-center gap-2 font-medium whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Bulletin
          </button>
        </div>
      </div>

      {/* Loader */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white border rounded-md p-4">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {
            bulletinsData?.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border border-borderGray rounded-md py-12">
              <p className="mb-4 text-lg font-semibold text-white">
                No bulletins have been created yet.
              </p>
              <p className="mb-6 text-sm text-gray-300">
                Use the "Add Bulletin" button above to create your first bulletin.
              </p>
            </div>
          ) :
          filtered.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border border-borderGray rounded-md py-12">
              <p className="mb-4 text-lg font-semibold text-white">
               No matching results found.
              </p>
              <p className="mb-6 text-sm text-gray-300">
              Try adjusting your search keywords.
              </p>
            </div>
          ) 
          : (
            <table className="min-w-full text-sm border border-gray-300 bg-transparent rounded-md shadow-sm">
              <thead>
                <tr className="bg-[#384355] text-left text-white text-base font-medium">
                  <th className="py-4 px-3 border-2 border-borderGray">Title</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Description</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Category</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Priority</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Attachments</th>
                  <th className="py-4 px-3 border-2 border-borderGray text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((bulletin, idx) => (
                  <tr key={idx} className="bg-transparent hover:bg-gray-50/10">
                    <td className="py-8 px-3 border-2 border-borderGray text-white font-medium">{bulletin.title}</td>
                    <td className="py-8 px-3 border-2 border-borderGray text-gray-300 truncate max-w-[300px]">
                      {bulletin.description || bulletin.message}
                    </td>
                    <td className="py-8 px-3 border-2 border-borderGray text-white">{bulletin.category}</td>
                    <td className="py-8 px-3 border-2 border-borderGray">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          bulletin.priority === 'high'
                            ? 'bg-red-100 text-red-700'
                            : bulletin.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {bulletin.priority.charAt(0).toUpperCase() + bulletin.priority.slice(1)}
                      </span>
                    </td>
                    <td className="py-8 px-3 border-2 border-borderGray text-white">
                      {(bulletin.attachments || []).length} files
                    </td>
                    <td className="py-8 px-3 border-2 border-borderGray text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => onEditBulletin(bulletin)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => onDeleteBulletin(bulletin._id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}